import { InfoCircleFilled } from '@ant-design/icons';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDebounce } from '@hooks';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useFormState } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';

import OpenNotification from 'src/app/components/antd/OpenNotification';
import { TabComponentProps } from 'src/app/components/antd/TabComponent';
import { tenantsApi } from 'src/features/tenants/api';
import {
  SettingsConfigType,
  TenantFormData,
  TenantFormTabs,
} from 'src/features/tenants/types/tenantForm.types';

import { TENANT_FORM_TABS } from '../constants/tenantFormTabs.const';
import { createTenantRequestFactory } from '../factories/createTenantRequestFactory';
import { editTenantRequestFactory } from '../factories/editTenantRequestFactory';
import { useVerifyTenantAlias } from './useVerifyTenantAlias';

const domainRegex = /^(?!:\/\/)([a-zA-Z0-9-_]+\.)+[a-zA-Z]{2,}$/;

export function useTenantForm() {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const isEditing = !!id;
  const [activeTab, setActiveTab] = useState<TenantFormTabs>('initialData');

  const headerInfo = useMemo(
    () => ({
      title: isEditing ? 'Editar tenant' : 'Criar tenant',
      subTitle: isEditing
        ? 'Edite as informações do tenant.'
        : 'Cadastre um novo tenant.',
    }),
    [isEditing]
  );

  const {
    isValid: isValidTenantAlias,
    handleChangeTenantAlias,
    isLoading: isLoadingTenantAliasVerify,
  } = useVerifyTenantAlias();

  const initialDataSchema = yup.object({
    tenantAliasId: yup
      .string()
      .required('O campo Tenant Alias é obrigatório')
      .min(3, 'O campo Tenant Alias deve ter no mínimo 3 caracteres')
      .test(
        'tenant-alias-exists',
        'Esse tenantAliasId já existe.',
        (value: string | undefined): boolean => {
          if (isEditing) return true;
          if (!value) return false;
          return isValidTenantAlias ?? false;
        }
      ),
    fantasyName: yup.string().required('O campo Nome Fantasia é obrigatório'),
    socialName: yup.string().required('O campo Razão Social é obrigatório'),
    document: yup
      .string()
      .test('valid-document', 'Insira um CNPJ válido', function (value) {
        if (!value) return false;
        const rawCnpj = value.replace(/\D/g, '');
        return rawCnpj.length === 14;
      }),
    description: yup.string().optional(),
  });

  const settingSchema = yup.object({
    id: yup.number().optional(),
    configKeyContext: yup
      .string()
      .nullable()
      .required('O campo Contexto é obrigatório'),
    configKey: yup.string().nullable().required('O campo Chave é obrigatório'),
    configType: yup
      .mixed<SettingsConfigType>()
      .nullable()
      .required('O campo Tipo é obrigatório'),
    configValue: yup
      .string()
      .nullable()
      .required('O campo Valor é obrigatório')
      .test('valid-value', 'Valor inválido', function (value) {
        const { encrypted } = this.parent;
        if (encrypted === 'true' && value) {
          const isValid = value.match(/^[a-zA-Z0-9+/=]+$/);
          return isValid !== null;
        }
        return true;
      }),
    configKeyDescription: yup
      .string()
      .nullable()
      .required('O campo Descrição é obrigatório'),
    encrypted: yup
      .string()
      .nullable()
      .required('O campo Criptografia é obrigatório'),
    public: yup
      .string()
      .nullable()
      .required('O campo visibilidade é obrigatório'),
    isEdit: yup.boolean().optional(),
  });

  const settingsSchema = yup.object({
    add: yup.array().of(settingSchema).optional(),
    edit: yup.array().of(settingSchema).optional(),
    delete: yup.array().of(yup.number()).optional(),
  });

  const domainSchema = yup.object({
    id: yup.number().optional(),
    isPrimary: yup.boolean().required(),
    domain: yup
      .string()
      .nullable()
      .matches(domainRegex, 'Insira um domínio válido'),
  });

  const domainsSchema = yup
    .object({
      add: yup.array().of(domainSchema).optional(),
      edit: yup.array().of(domainSchema).optional(),
      delete: yup.array().of(yup.number()).optional(),
    })
    .test(
      'at-least-one-primary-domain',
      'Defina um domínio como principal',
      (items, context) => {
        const deletedDomains = context.parent.domains.delete ?? [];
        const domainsToVerify = [
          ...(items.add?.map(item => Boolean(item.isPrimary)) ?? []),
          ...(items.edit
            ?.filter(itemsEdit => !deletedDomains.includes(itemsEdit.id))
            .map(item => Boolean(item.isPrimary)) ?? []),
        ];
        return domainsToVerify?.some(item => item === true) ?? false;
      }
    );

  const tenantFormValidationSchema = yup.object({
    initialData: initialDataSchema,
    settings: settingsSchema.optional(),
    domains: domainsSchema,
  });

  type TenantFormValidated = yup.InferType<typeof tenantFormValidationSchema>;

  const tenantFormMethods = useForm<TenantFormValidated>({
    resolver: yupResolver(tenantFormValidationSchema),
    defaultValues: {
      domains: {
        add: [{ domain: '', isPrimary: true }],
      },
    },
  });
  const { handleSubmit, control, trigger, reset, watch } = tenantFormMethods;
  const { errors: formErrors, dirtyFields } = useFormState<TenantFormValidated>(
    {
      control,
    }
  );

  const isLoadingFields = useMemo(() => {
    return {
      initialData: {
        tenantAliasId: isLoadingTenantAliasVerify,
      },
    };
  }, [isLoadingTenantAliasVerify]);

  const [tenantAlias, deletedDomains, deletedSettings] = watch([
    'initialData.tenantAliasId',
    'domains.delete',
    'settings.delete',
  ]);

  const tenantAliasDebounced = useDebounce(tenantAlias, 900);

  useEffect(() => {
    if (
      !isEditing &&
      (tenantAliasDebounced.length > 2 || tenantAliasDebounced.length === 0)
    )
      handleChangeTenantAlias(tenantAliasDebounced);
  }, [tenantAliasDebounced]);

  useEffect(() => {
    if (!isEditing && tenantAlias?.length > 2)
      trigger('initialData.tenantAliasId');
  }, [isValidTenantAlias]);

  const isModifiedOnEdit = useMemo(() => {
    if (!isEditing) return false;
    const initialDataDirty =
      dirtyFields.initialData &&
      Object.values(dirtyFields.initialData).some(dirtyField => !!dirtyField);

    const domainsDirty =
      (dirtyFields.domains?.edit &&
        dirtyFields.domains.edit.filter(setting =>
          Object.values(setting).some(dirtyField => !!dirtyField)
        ).length > 0) ||
      (dirtyFields.domains?.add && dirtyFields.domains.add.length > 0) ||
      (deletedDomains && deletedDomains?.length > 0);

    const settingsDirty =
      (dirtyFields.settings?.edit &&
        dirtyFields.settings.edit.filter(setting =>
          Object.values(setting).some(dirtyField => !!dirtyField)
        ).length > 0) ||
      (dirtyFields.settings?.add && dirtyFields.settings.add.length > 0) ||
      (deletedSettings && deletedSettings?.length > 0);

    return initialDataDirty || domainsDirty || settingsDirty;
  }, [
    dirtyFields.initialData,
    dirtyFields.domains,
    dirtyFields.settings,
    deletedDomains,
    deletedSettings,
  ]);

  const tabError = useMemo<TenantFormTabs[] | undefined>(() => {
    const errors = Object.keys(formErrors).filter(
      errorKey =>
        errorKey !== 'root' &&
        !!formErrors[errorKey as keyof TenantFormValidated] &&
        errorKey !== activeTab
    ) as TenantFormTabs[];
    return errors.length > 0 ? errors : undefined;
  }, [formErrors, activeTab]);

  const changeActiveTab = (tab: TenantFormTabs) => {
    trigger();
    setActiveTab(tab);
  };

  const tenantTabList: TabComponentProps['tabList'] = useMemo(
    () =>
      Object.keys(TENANT_FORM_TABS).map(tenantTabKey => {
        const hasError = tabError?.includes(tenantTabKey as TenantFormTabs);
        return {
          title: TENANT_FORM_TABS[tenantTabKey as TenantFormTabs],
          key: tenantTabKey,
          icon: hasError ? (
            <InfoCircleFilled style={{ fontSize: 10 }} />
          ) : undefined,
          style: hasError ? { color: 'red' } : {},
        };
      }),
    [tabError]
  );

  const inferConfigTypeByValue = useCallback((value: string) => {
    if (value === 'true' || value === 'false')
      return SettingsConfigType.Boolean;
    if (!isNaN(Number(value))) return SettingsConfigType.Number;
    return SettingsConfigType.String;
  }, []);

  const { isPending: isLoadingEditData } = useQuery({
    queryKey: [`tenant-edit-data-${id}`],
    queryFn: async () => {
      if (!id) return;
      const tenantData = await tenantsApi.getTenantById(Number(id));
      reset({
        initialData: {
          description: tenantData.initialData.description,
          document: tenantData.initialData.document,
          fantasyName: tenantData.initialData.fantasyName,
          socialName: tenantData.initialData.socialName,
          tenantAliasId: tenantData.initialData.tenantAliasId,
        },
        settings: {
          edit: tenantData.settings.map(setting => ({
            ...setting,
            id: Number(setting.id),
            configType: inferConfigTypeByValue(setting.configValue),
            encrypted: String(setting.encrypted),
            public: String(setting.public),
          })),
        },
        domains: {
          edit: tenantData.domains.map(domain => ({
            ...domain,
            id: Number(domain.id),
          })),
        },
      });
    },
    enabled: !!id,
    refetchOnWindowFocus: false,
  });

  const handleTenantFormSubmit = () => {
    handleSubmit(
      (data: TenantFormValidated) => submitTenantForm(data as TenantFormData),
      () => {
        OpenNotification(
          true,
          'Ocorreu um erro',
          'Corrija os erros presentes no formulário e tente novamente!'
        );
      }
    )();
  };

  const { mutate: submitTenantForm, isPending: isLoadingSubmit } = useMutation({
    mutationFn: async (data: TenantFormData) => {
      if (isEditing) {
        const dataToSubmit = { ...data };
        const settingsEditedFields = data.settings?.edit?.filter(
          (setting, index) => {
            const isDirty = dirtyFields.settings?.edit?.[index];
            const isDeleted = data.settings?.delete?.includes(setting.id ?? -1);
            return (
              isDirty && !isDeleted && Object.values(isDirty).some(Boolean)
            );
          }
        );
        const domainsEditedFields = data.domains?.edit?.filter(
          (domain, index) => {
            const isDirty = dirtyFields.domains?.edit?.[index];
            const isDeleted = data.domains?.delete?.includes(domain.id ?? -1);
            return (
              isDirty && !isDeleted && Object.values(isDirty).some(Boolean)
            );
          }
        );
        dataToSubmit.settings = {
          ...data.settings,
          edit: settingsEditedFields,
        };
        dataToSubmit.domains = {
          ...data.domains,
          edit: domainsEditedFields,
        };

        const payload = editTenantRequestFactory(dataToSubmit);
        await tenantsApi.editTenant(Number(id), payload);
      } else {
        const payload = createTenantRequestFactory(data);
        if (!payload) return;
        await tenantsApi.createTenant(payload);
      }
    },
    onSuccess: () => {
      handleGoBackToTenantList();
      OpenNotification(
        false,
        'Sucesso!',
        `Tenant ${isEditing ? 'editado' : 'criado'} com sucesso.`
      );
    },
    onError: error => {
      console.error(error);
      OpenNotification(
        true,
        'Ocorreu um erro',
        'Tivemos um problema, por favor acione nosso suporte.'
      );
    },
  });

  const handleGoBackToTenantList = () => {
    navigate('/tenants');
  };

  return {
    isEditing,
    isLoadingEditData,
    headerInfo,
    activeTab,
    tenantTabList,
    isLoadingSubmit,
    tenantFormMethods,
    changeActiveTab,
    handleTenantFormSubmit,
    handleGoBackToTenantList,
    isModifiedOnEdit,
    isLoadingFields,
  };
}
